import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import MediaQuery from 'react-responsive';
import { ModalProps as BaseModalProps } from 'reactstrap';

import {
  useRfqBuilderModalMethods,
  useRfqBuilderModalState,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/modal-context';
import { ANON_RFQ_MIN_ALLOWED_COUNTERPARIES } from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/view';
import {
  RfqBuilderProvider,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';

import LiquidityProviderIcon from '@paradigm/design-system/src/assets/LiquidityProviderIcon';
import BaseModal, {
  CloseButton,
  Footer as BaseFooter,
} from '@paradigm/design-system/src/components/Modal';
import { default as DefaultGrid } from '#/components/UnifiedRfqs/components/Grid';
import { toast, TOAST_TIMEOUT_LONG } from '#/components/UnifiedRfqs/toast';
import { selectIsAppOffline } from '#/redux/selectors/app';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import anonymousGuy from '#/assets/anonymous-guy.png';
import {
  blackPearl_4,
  portage0,
  portage20,
} from '@paradigm/design-system/src/colors';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { SelectContainer } from '#/components/UnifiedRfqs/components/SelectField';
import ButtonGrid from '#/components/UnifiedRfqs/rfq-builder/components/ButtonGrid';
import LegsTable from '#/components/UnifiedRfqs/rfq-builder/legs/LegsTable';
import VenueSelector from '#/components/UnifiedRfqs/rfq-builder/VenueSelector';
import ProductSelector from '#/components/UnifiedRfqs/rfq-builder/ProductSelector';
import ProductButtons from '#/components/UnifiedRfqs/rfq-builder/ProductButtons';
import StrategySelector from '#/components/UnifiedRfqs/rfq-builder/StrategySelector';
import StrategyButtons from '#/components/UnifiedRfqs/rfq-builder/StrategyButtons';
import AuctionButtons from '#/components/UnifiedRfqs/rfq-builder/AuctionButtons';
import SubmitRfqButton from '#/components/UnifiedRfqs/rfq-builder/SubmitRfqButton';
import SaveDraftButton from '#/components/UnifiedRfqs/rfq-builder/SaveDraftButton';
import { H1 } from '#/components/UnifiedRfqs/rfq-builder/components/primitives';
import StrategyOptionGreeks from '#/components/UnifiedRfqs/rfq-builder/components/StrategyOptionsGreeks/StrategyOptionGreeks';
import CounterpartiesSelector from '#/components/UnifiedRfqs/rfq-builder/counterparties/CounterpartiesSelector';
import AnonymousSwitch from '#/components/UnifiedRfqs/rfq-builder/AnonymousSwitch';
import HiddenLimitOrderFields from '#/components/UnifiedRfqs/rfq-builder/HiddenLimitOrderFields';
import Warning from '#/components/UnifiedRfqs/rfq-builder/components/Warning';
import AccountField from '#/components/UnifiedRfqs/rfq-builder/AccountField';
import { WarningMessage } from '#/components/UnifiedRfqs/components/WarningMessage';
import { TrackingCtxProvider } from '@paradigm/features/src/analytics';

export default function RfqBuilder() {
  const { isOpen, prefill } = useRfqBuilderModalState();

  if (!isOpen) return <div css="visibility: none;" />;

  return (
    <RfqBuilderProvider
      prefill={prefill}
      onPrefillError={(message) =>
        toast(message, { autoClose: TOAST_TIMEOUT_LONG })
      }
    >
      <TrackingCtxProvider value="market builder">
        <RfqBuilderContent />
      </TrackingCtxProvider>
    </RfqBuilderProvider>
  );
}

function RfqBuilderContent() {
  const { isOpen } = useRfqBuilderModalState();
  const { close } = useRfqBuilderModalMethods();
  const { anonymous, auction, isOrderBook } = useRfqBuilderView();
  const isAppOffline = useSelector<unknown, boolean>(selectIsAppOffline);

  const isRfqAuction = auction.selectedOption.value === 'RFQ';
  const isAnonymousBackgroundVisible = anonymous && isRfqAuction;

  const classes = classNames({
    blur: isAppOffline,
  });

  const isMobile = useIsMobile();
  const accountField = !isOrderBook && <AccountField />;
  const divider = isMobile && <Divider />;
  const counterpartiesSelector = isRfqAuction && (
    <>
      {divider}
      <CounterpartiesSelector />
    </>
  );
  const backdrop = isMobile ? 'static' : true;
  const warning = <Warning />;

  return (
    <Modal
      width="auto"
      isOpen={isOpen}
      toggle={close}
      aria-label="unified rfq rfq builder modal"
      backdrop={backdrop}
      isAnonymousBackgroundVisible={isAnonymousBackgroundVisible}
      className={classes}
    >
      <Body>
        <CloseButton
          onClick={close}
          css="position: absolute; top: 21px; right: 16px;"
        />
        <Main isOrderBook={isOrderBook}>
          <Grid>
            <Title>Market Builder</Title>
            <Grid>
              <Grid cols={isMobile ? 1 : 2}>
                <Grid>
                  <VenueSelector />
                  <MediaQuery maxWidth={MOBILE_BREAKPOINT}>
                    {accountField}
                  </MediaQuery>
                  <ProductSelector />
                  <ButtonGrid aria-label="Select product">
                    <ProductButtons />
                  </ButtonGrid>
                  <StrategySelector />
                  <ButtonGrid aria-label="Select strategy">
                    <StrategyButtons />
                  </ButtonGrid>
                  {divider}
                  <AuctionButtonsGrid
                    role="group"
                    aria-label="Select auction type"
                  >
                    <AuctionButtons />
                  </AuctionButtonsGrid>
                </Grid>
                {divider}
                <Grid>
                  <MediaQuery minWidth={MOBILE_BREAKPOINT}>
                    {accountField}
                  </MediaQuery>
                  <StrategyOptionGreeks />
                </Grid>
                {divider}
              </Grid>
            </Grid>
            <LegsTable />
            <MediaQuery maxWidth={MOBILE_BREAKPOINT}>{warning}</MediaQuery>
          </Grid>
        </Main>
        {counterpartiesSelector}
        {divider}
      </Body>
      <Footer isOrderBook={isOrderBook}>
        <FooterFormContainer>
          <ToggleContainer>
            <AnonymousSwitch />
            <HiddenLimitOrderFields />
          </ToggleContainer>
          {anonymous && !isOrderBook && (
            <WarningContainer>
              <WarningMessage>
                Select at least {ANON_RFQ_MIN_ALLOWED_COUNTERPARIES}x LPs
                <LiquidityProviderIcon
                  color="white"
                  style={{ marginLeft: '4px', marginRight: '4px' }}
                  width={13}
                  height={13}
                />
                for Anonymous RFQs
              </WarningMessage>
            </WarningContainer>
          )}
          <MediaQuery minWidth={MOBILE_BREAKPOINT}>{warning}</MediaQuery>
        </FooterFormContainer>

        <SubmitContainer isOrderBook={isOrderBook}>
          {isRfqAuction && <SaveDraftButton />}
          <SubmitRfqButton />
        </SubmitContainer>
      </Footer>
    </Modal>
  );
}

const FooterFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const WarningContainer = styled.div`
  display: flex;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    max-width: 500px;
  }
`;

const AuctionButtonsGrid = styled.div`
  display: flex;
  & > * {
    flex: 1;
  }
`;

const Footer = styled(BaseFooter)<{ isOrderBook: boolean }>`
  position: relative;
  display: flex;
  padding: 1rem;
  flex-direction: row;
  justify-content: ${(props) =>
    props.isOrderBook ? 'space-between' : 'flex-start'};
  align-items: center;
  min-height: 67px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 1rem 1rem 2rem;
    flex-direction: ${(props) => (props.isOrderBook ? 'space-between' : 'row')};
    flex-wrap: ${(props) => (props.isOrderBook ? 'nowrap' : 'wrap')};
    align-items: ${(props) => (props.isOrderBook ? 'center' : 'flex-start')};
    min-height: unset;
  }
`;

const Main = styled.div<{ isOrderBook: boolean }>`
  width: ${({ isOrderBook }) => (isOrderBook ? '712px' : '898px')};
  padding: 1rem 1rem 0 1rem;
`;

const SubmitContainer = styled.div<{ isOrderBook: boolean }>`
  position: ${(props) => (props.isOrderBook ? 'inherit' : 'absolute')};
  right: 1rem;
  align-self: flex-start;
  border-radius: 16px;
  display: flex;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    position: static;
    right: unset;
    margin-top: 1rem;
    align-self: flex-start;
    width: 100%;
  }
`;

const Grid = styled(DefaultGrid)`
  ${SelectContainer} {
    margin-bottom: 0;
  }
  ${ButtonGrid} {
    margin-bottom: -0.4rem;
    padding-top: 0.2rem;
  }
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 1.5rem;
  }
`;

const Divider = styled.div`
  background: linear-gradient(to right, ${portage0}, ${portage20}, ${portage0});
  height: 2px;
  width: 100%;
  margin: 0.5rem 0;
`;

const Title = styled(H1)`
  margin-bottom: 0;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    text-align: center;
  }
`;

const Body = styled.div`
  position: relative;
  display: flex;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: grid;
    flex: 1 1 auto;
    align-content: flex-start;
    gap: 1rem;

    ${Main} {
      width: auto;
    }
    .select-control {
      height: 40px;
    }
    .select-value-container {
      > div {
        font-size: 1rem;
      }
    }
  }
`;

type ModalProps = {
  isAnonymousBackgroundVisible: boolean;
} & BaseModalProps;

const Modal = styled(BaseModal).withConfig<ModalProps>({
  shouldForwardProp: (prop) => prop !== 'isAnonymousBackgroundVisible',
})`
  .modal-content {
    width: auto;
  }

  ${(props) =>
    props.isAnonymousBackgroundVisible &&
    css`
      .modal-content {
        overflow: visible;
        width: auto;
        background-color: ${blackPearl_4} !important;
        background-image: url(${anonymousGuy});
        background-repeat: no-repeat;
        background-position: center;
      }
    `}

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    ${ButtonGrid}, ${AuctionButtonsGrid} {
      button {
        height: auto;
        line-height: 2rem;
      }
    }

    ${Footer} {
      font-size: 1rem;
      line-height: 2rem;

      button {
        font-size: 1rem;
        line-height: 2rem;
        height: auto;
      }

      label {
        margin: 0;
      }
    }
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;
